// Mock data for SpecLedger demo

export interface Project {
  id: string;
  name: string;
  repo: string;
  lastUpdated: string;
  phases: {
    specify: 'not-started' | 'in-progress' | 'complete';
    plan: 'not-started' | 'in-progress' | 'complete';
    tasks: 'not-started' | 'in-progress' | 'complete';
  };
  activeSpec?: string;
}

export interface Artifact {
  name: string;
  path: string;
  isCore?: boolean;
  lastUpdated?: string;
}

export interface Issue {
  id: string;
  type: 'Epic' | 'Feature' | 'Task';
  priority: 'P0' | 'P1' | 'P2' | 'P3' | 'P4';
  title: string;
  status: 'Blocked' | 'Ready' | 'In Progress' | 'Closed';
  labels: string[];
  blockedBy?: string[];
  blocks?: string[];
  comments?: number;
  children?: Issue[];
}

export interface Change {
  file: string;
  additions: number;
  deletions: number;
  diff: string;
}

export interface Commit {
  hash: string;
  author: string;
  timestamp: string;
  message: string;
  changes: Change[];
}

// Projects data
export const projects: Project[] = [
  {
    id: 'nguyenha-backend',
    name: 'nguyenha-backend',
    repo: 'Rockship-Team/nguyenha-backend',
    lastUpdated: 'about 3 hours ago',
    phases: { specify: 'complete', plan: 'complete', tasks: 'in-progress' },
    activeSpec: '001-authentication',
  },
  {
    id: 'specledger-core',
    name: 'specledger-core',
    repo: 'specledger/core',
    lastUpdated: 'about 1 day ago',
    phases: { specify: 'complete', plan: 'in-progress', tasks: 'not-started' },
    activeSpec: '003-issue-tracker',
  },
  {
    id: 'mobile-app',
    name: 'mobile-app',
    repo: 'acme-corp/mobile-app',
    lastUpdated: 'about 2 days ago',
    phases: { specify: 'in-progress', plan: 'not-started', tasks: 'not-started' },
    activeSpec: '001-onboarding-flow',
  },
  {
    id: 'data-pipeline',
    name: 'data-pipeline',
    repo: 'analytics/data-pipeline',
    lastUpdated: 'about 5 days ago',
    phases: { specify: 'complete', plan: 'complete', tasks: 'complete' },
    activeSpec: '002-etl-optimization',
  },
];

// Artifacts organized by stage
export const artifacts: Record<string, Artifact[]> = {
  specify: [
    { name: 'spec.md', path: 'specs/001-authentication', isCore: true, lastUpdated: '2 hours ago' },
    { name: 'requirements.md', path: 'specs/001-authentication/checklists', lastUpdated: '3 hours ago' },
    { name: 'acceptance-criteria.md', path: 'specs/001-authentication/checklists', lastUpdated: '3 hours ago' },
    { name: 'user-stories.md', path: 'specs/001-authentication/checklists', lastUpdated: '1 day ago' },
  ],
  plan: [
    { name: 'plan.md', path: 'specs/001-authentication', isCore: true, lastUpdated: '4 hours ago' },
    { name: 'research.md', path: 'specs/001-authentication', lastUpdated: '1 day ago' },
    { name: 'data-model.md', path: 'specs/001-authentication', lastUpdated: '6 hours ago' },
    { name: 'quickstart.md', path: 'specs/001-authentication', lastUpdated: '5 hours ago' },
    { name: 'api-design.md', path: 'specs/001-authentication', lastUpdated: '8 hours ago' },
  ],
  tasks: [
    { name: 'tasks.md', path: 'specs/001-authentication', isCore: true, lastUpdated: '1 hour ago' },
  ],
};

// Issues data with hierarchical structure
export const issues: Issue[] = [
  {
    id: 'trk-abc',
    type: 'Epic',
    priority: 'P1',
    title: 'User Authentication System',
    status: 'In Progress',
    labels: ['epic:auth', 'spec:001-authentication'],
    comments: 12,
    children: [
      {
        id: 'trk-def',
        type: 'Feature',
        priority: 'P1',
        title: 'Email/Password Authentication',
        status: 'Closed',
        labels: ['feature:email-auth', 'spec:001-authentication'],
        comments: 8,
        children: [
          {
            id: 'trk-ghi',
            type: 'Task',
            priority: 'P2',
            title: 'Implement password hashing with bcrypt',
            status: 'Closed',
            labels: ['component:auth-service', 'spec:001-authentication'],
            comments: 3,
          },
          {
            id: 'trk-jkl',
            type: 'Task',
            priority: 'P2',
            title: 'Add email validation endpoint',
            status: 'Closed',
            labels: ['component:handler', 'spec:001-authentication'],
            comments: 2,
          },
        ],
      },
      {
        id: 'trk-mno',
        type: 'Feature',
        priority: 'P2',
        title: 'OAuth 2.0 Integration',
        status: 'In Progress',
        labels: ['feature:oauth', 'spec:001-authentication'],
        comments: 5,
        children: [
          {
            id: 'trk-pqr',
            type: 'Task',
            priority: 'P2',
            title: 'Configure Google OAuth provider',
            status: 'In Progress',
            labels: ['component:oauth', 'spec:001-authentication'],
            comments: 4,
          },
          {
            id: 'trk-stu',
            type: 'Task',
            priority: 'P3',
            title: 'Configure GitHub OAuth provider',
            status: 'Ready',
            labels: ['component:oauth', 'spec:001-authentication'],
            comments: 1,
          },
          {
            id: 'trk-vwx',
            type: 'Task',
            priority: 'P3',
            title: 'Implement OAuth callback handler',
            status: 'Blocked',
            labels: ['component:handler', 'spec:001-authentication'],
            blockedBy: ['trk-pqr'],
            comments: 0,
          },
        ],
      },
      {
        id: 'trk-yza',
        type: 'Feature',
        priority: 'P2',
        title: 'Session Management',
        status: 'Ready',
        labels: ['feature:sessions', 'spec:001-authentication'],
        comments: 3,
        children: [
          {
            id: 'trk-bcd',
            type: 'Task',
            priority: 'P2',
            title: 'Implement JWT token generation',
            status: 'Ready',
            labels: ['component:auth-service', 'spec:001-authentication'],
            comments: 2,
          },
          {
            id: 'trk-efg',
            type: 'Task',
            priority: 'P3',
            title: 'Add refresh token rotation',
            status: 'Blocked',
            labels: ['component:auth-service', 'spec:001-authentication'],
            blockedBy: ['trk-bcd'],
            comments: 0,
          },
        ],
      },
    ],
  },
];

// Flatten issues for Kanban view
export function flattenIssues(issueList: Issue[]): Issue[] {
  const flat: Issue[] = [];
  function traverse(items: Issue[]) {
    for (const item of items) {
      flat.push(item);
      if (item.children) {
        traverse(item.children);
      }
    }
  }
  traverse(issueList);
  return flat;
}

// Artifact content for the viewer
export const artifactContent: Record<string, string> = {
  'spec.md': `# Authentication Specification

## Overview

This specification defines the authentication system for the nguyenha-backend application. The system will support multiple authentication methods including email/password and OAuth providers.

## User Stories

### Authentication Methods
- As a user, I can login with email and password
- As a user, I can login with Google OAuth
- As a user, I can login with GitHub OAuth
- As a user, I can logout from all sessions
- As a user, I can view my active sessions

### Password Management
- As a user, I can reset my password via email
- As a user, I can change my password when logged in
- As a user, I am notified when my password is changed

### Session Management
- Sessions expire after 7 days of inactivity
- Users can have up to 5 concurrent sessions
- Refresh tokens rotate on each use

## Technical Requirements

### Authentication Flow
- JWT access tokens (15 min expiry)
- Refresh tokens (7 day expiry)
- Secure HTTP-only cookies for token storage

### Security Requirements
- Password hashing with bcrypt (cost factor 12)
- Rate limiting on login attempts
- CSRF protection for all endpoints
- Audit logging for authentication events

### Data Model

\`\`\`typescript
interface User {
  id: string;
  email: string;
  passwordHash: string;
  createdAt: Date;
  updatedAt: Date;
}

interface Session {
  id: string;
  userId: string;
  refreshToken: string;
  expiresAt: Date;
  createdAt: Date;
}
\`\`\`

## Acceptance Criteria

1. Users can register with email and password
2. Users can login with valid credentials
3. Invalid login attempts are rate limited
4. Sessions can be invalidated individually or all at once
5. OAuth flow completes successfully for Google and GitHub
`,

  'plan.md': `# Implementation Plan

## Overview

This plan outlines the implementation strategy for the authentication system defined in \`spec.md\`.

## Implementation Phases

### Phase 1: Core Authentication
- [x] Password hashing with bcrypt
- [x] Email validation endpoint
- [x] JWT token generation
- [x] Login/logout endpoints

### Phase 2: OAuth Integration (In Progress)
- [x] OAuth provider abstraction
- [ ] Google OAuth provider (in progress)
- [ ] GitHub OAuth provider (blocked)
- [ ] OAuth callback handler

### Phase 3: Session Management
- [ ] Refresh token rotation
- [ ] Session listing endpoint
- [ ] Concurrent session limits

## Architecture Decisions

### Token Strategy
We chose JWT for access tokens because:
1. Stateless verification reduces database load
2. Short expiry limits damage from token theft
3. Refresh tokens provide seamless UX

### OAuth Provider Abstraction
Created a generic provider interface to:
- Simplify adding new OAuth providers
- Standardize token handling
- Enable easy testing with mocks

## Dependencies

| Package | Version | Purpose |
|---------|---------|---------|
| bcrypt | ^5.1.0 | Password hashing |
| jsonwebtoken | ^9.0.0 | JWT generation/verification |
| passport | ^0.6.0 | OAuth integration |

## Risks and Mitigations

> **Risk**: Token theft could compromise accounts
> **Mitigation**: Short access token expiry, refresh token rotation

> **Risk**: OAuth provider downtime
> **Mitigation**: Graceful fallback to email/password
`,

  'requirements.md': `# Requirements Checklist

## Functional Requirements

### Authentication
- [ ] REQ-001: Email/password registration
- [x] REQ-002: Email/password login
- [ ] REQ-003: Google OAuth login
- [ ] REQ-004: GitHub OAuth login
- [x] REQ-005: Logout functionality

### Session Management
- [ ] REQ-006: Session listing
- [ ] REQ-007: Session revocation
- [ ] REQ-008: Concurrent session limits

### Password Management
- [ ] REQ-009: Password reset flow
- [ ] REQ-010: Password change
- [ ] REQ-011: Password strength validation

## Non-Functional Requirements

### Security
- [x] NFR-001: Passwords hashed with bcrypt
- [x] NFR-002: JWT tokens for sessions
- [ ] NFR-003: Rate limiting implemented
- [ ] NFR-004: CSRF protection enabled

### Performance
- [ ] NFR-005: Login < 200ms p95
- [ ] NFR-006: Token validation < 10ms
`,

  'tasks.md': `# Task Tracking

## Current Sprint

### In Progress
- [ ] Configure Google OAuth provider (trk-pqr)
  - Setting up OAuth credentials
  - Implementing token exchange

### Blocked
- [ ] Configure GitHub OAuth provider (trk-stu)
  - Blocked by: Google OAuth completion
- [ ] Implement OAuth callback handler (trk-vwx)
  - Blocked by: Provider configuration

### Ready
- [ ] Implement JWT token generation (trk-bcd)
- [ ] Add refresh token rotation (trk-efg)

## Completed
- [x] Implement password hashing with bcrypt (trk-ghi)
- [x] Add email validation endpoint (trk-jkl)

## Notes

The OAuth integration is taking longer than expected due to:
1. Provider documentation inconsistencies
2. Testing environment setup complexity
`,

  'research.md': `# OAuth 2.0 Research

## Provider Comparison

### Google OAuth
- Most widely used
- Good documentation
- Supports OIDC
- Required scopes: \`openid\`, \`email\`, \`profile\`

### GitHub OAuth
- Popular for developer tools
- Simple API
- No OIDC support
- Required scopes: \`user:email\`

## Token Strategy

Recommended approach:
1. Short-lived access tokens (15 min)
2. Longer refresh tokens (7 days)
3. Rotate refresh tokens on use

## Security Considerations

### Token Storage
- Access tokens: Memory only
- Refresh tokens: HTTP-only cookies
- Never store in localStorage

### PKCE Flow
Consider implementing PKCE for:
- Mobile applications
- Single-page applications
- Additional security layer
`,

  'data-model.md': `# Data Model

## Entity Relationship Diagram

\`\`\`
User 1:N Session
User 1:N OAuthConnection
\`\`\`

## Tables

### users
| Column | Type | Constraints |
|--------|------|-------------|
| id | UUID | PRIMARY KEY |
| email | VARCHAR(255) | UNIQUE, NOT NULL |
| password_hash | VARCHAR(255) | NULL (for OAuth users) |
| created_at | TIMESTAMP | NOT NULL |
| updated_at | TIMESTAMP | NOT NULL |

### sessions
| Column | Type | Constraints |
|--------|------|-------------|
| id | UUID | PRIMARY KEY |
| user_id | UUID | FOREIGN KEY |
| refresh_token | VARCHAR(255) | UNIQUE |
| expires_at | TIMESTAMP | NOT NULL |
| created_at | TIMESTAMP | NOT NULL |

### oauth_connections
| Column | Type | Constraints |
|--------|------|-------------|
| id | UUID | PRIMARY KEY |
| user_id | UUID | FOREIGN KEY |
| provider | VARCHAR(50) | NOT NULL |
| provider_user_id | VARCHAR(255) | NOT NULL |
| created_at | TIMESTAMP | NOT NULL |

## Indexes

- \`users.email\` - Unique index for login lookup
- \`sessions.user_id\` - For session listing
- \`sessions.refresh_token\` - For token validation
- \`oauth_connections(user_id, provider)\` - Unique constraint
`,

  'api-design.md': `# API Design

## Endpoints

### POST /auth/register
Register a new user with email and password.

**Request:**
\`\`\`json
{
  "email": "user@example.com",
  "password": "securepassword123"
}
\`\`\`

**Response:** \`201 Created\`
\`\`\`json
{
  "user": {
    "id": "uuid",
    "email": "user@example.com"
  }
}
\`\`\`

### POST /auth/login
Authenticate with email and password.

**Request:**
\`\`\`json
{
  "email": "user@example.com",
  "password": "securepassword123"
}
\`\`\`

**Response:** \`200 OK\`
\`\`\`json
{
  "accessToken": "jwt...",
  "expiresIn": 900
}
\`\`\`

### POST /auth/logout
Invalidate the current session.

**Response:** \`204 No Content\`

### GET /auth/sessions
List all active sessions for the current user.

**Response:** \`200 OK\`
\`\`\`json
{
  "sessions": [
    {
      "id": "uuid",
      "createdAt": "2024-01-15T10:00:00Z",
      "lastActive": "2024-01-15T12:00:00Z"
    }
  ]
}
\`\`\`

### DELETE /auth/sessions/:id
Revoke a specific session.

**Response:** \`204 No Content\`

## Error Responses

All errors follow this format:
\`\`\`json
{
  "error": {
    "code": "INVALID_CREDENTIALS",
    "message": "Email or password is incorrect"
  }
}
\`\`\`
`,

  'quickstart.md': `# Quickstart Guide

## Getting Started

### Prerequisites
- Node.js 18+
- PostgreSQL 14+
- Redis (for rate limiting)

### Installation

\`\`\`bash
# Clone the repository
git clone https://github.com/Rockship-Team/nguyenha-backend

# Install dependencies
npm install

# Copy environment file
cp .env.example .env

# Run migrations
npm run db:migrate

# Start development server
npm run dev
\`\`\`

### Environment Variables

\`\`\`env
DATABASE_URL=postgresql://user:pass@localhost:5432/db
JWT_SECRET=your-secret-key
GOOGLE_CLIENT_ID=your-google-client-id
GOOGLE_CLIENT_SECRET=your-google-client-secret
\`\`\`

## Testing

\`\`\`bash
# Run all tests
npm test

# Run with coverage
npm run test:coverage
\`\`\`

## Common Issues

### "Invalid token" errors
- Check JWT_SECRET is set correctly
- Verify token hasn't expired
- Ensure cookies are being sent with requests

### OAuth redirect issues
- Verify callback URLs in provider console
- Check CORS configuration
`,

  'acceptance-criteria.md': `# Acceptance Criteria

## User Registration

### AC-001: Successful Registration
**Given** a valid email and password
**When** the user submits the registration form
**Then** a new account is created and the user receives a confirmation email

### AC-002: Duplicate Email
**Given** an email that already exists in the system
**When** the user attempts to register
**Then** an error message is displayed indicating the email is taken

### AC-003: Weak Password
**Given** a password that doesn't meet requirements
**When** the user submits the registration form
**Then** an error message lists the missing requirements

## User Login

### AC-004: Successful Login
**Given** valid credentials
**When** the user submits the login form
**Then** they are redirected to the dashboard with a valid session

### AC-005: Invalid Credentials
**Given** incorrect email or password
**When** the user submits the login form
**Then** a generic error message is shown (not revealing which field is wrong)

### AC-006: Rate Limiting
**Given** 5 failed login attempts
**When** the user attempts another login
**Then** they are temporarily locked out with a countdown timer

## Session Management

### AC-007: Session Expiry
**Given** an inactive session for 7 days
**When** the user attempts to use the session
**Then** they are redirected to login

### AC-008: Concurrent Sessions
**Given** a user with 5 active sessions
**When** they attempt to create a 6th session
**Then** the oldest session is automatically revoked
`,

  'user-stories.md': `# User Stories

## Epic: User Authentication

### US-001: Email Registration
**As a** new user
**I want to** register with my email address
**So that** I can access the application

**Acceptance Criteria:**
- Email must be valid format
- Password must be at least 8 characters
- Confirmation email is sent
- Account is inactive until confirmed

### US-002: Email Login
**As a** registered user
**I want to** login with my email and password
**So that** I can access my account

**Acceptance Criteria:**
- Invalid credentials show generic error
- Rate limiting after 5 failed attempts
- Remember me option for extended sessions

### US-003: Google OAuth
**As a** user
**I want to** login with my Google account
**So that** I don't need to remember another password

**Acceptance Criteria:**
- Redirects to Google consent screen
- Creates account if first time
- Links to existing account if email matches

### US-004: Session Management
**As a** security-conscious user
**I want to** see and manage my active sessions
**So that** I can detect unauthorized access

**Acceptance Criteria:**
- List shows device/browser info
- Each session can be individually revoked
- "Logout all" option available

### US-005: Password Reset
**As a** user who forgot their password
**I want to** reset my password via email
**So that** I can regain access to my account

**Acceptance Criteria:**
- Reset link expires after 1 hour
- Old sessions are invalidated
- Notification email sent after reset
`,
};

// Recent commit with changes
export const recentCommit: Commit = {
  hash: 'a3f7b2c',
  author: 'Alice Chen',
  timestamp: '2 hours ago',
  message: 'feat(auth): add OAuth 2.0 support with Google provider',
  changes: [
    {
      file: 'spec.md',
      additions: 24,
      deletions: 8,
      diff: [
        '@@ -15,8 +15,20 @@ ## User Stories',
        ' ',
        ' ### Authentication Methods',
        ' - As a user, I can login with email and password',
        '-- As a user, I can logout from my account',
        '+- As a user, I can login with Google OAuth',
        '+- As a user, I can login with GitHub OAuth',
        '+- As a user, I can logout from all sessions',
        '+- As a user, I can view my active sessions',
        ' ',
        '+### Session Management',
        '+- Sessions expire after 7 days of inactivity',
        '+- Users can have up to 5 concurrent sessions',
        '+- Refresh tokens rotate on each use',
        ' ',
        ' ## Technical Requirements',
        '-- JWT tokens for session management',
        '+- JWT access tokens (15 min expiry)',
        '+- Refresh tokens (7 day expiry)',
      ].join('\n'),
    },
    {
      file: 'plan.md',
      additions: 15,
      deletions: 3,
      diff: [
        '@@ -8,9 +8,21 @@ ## Implementation Phases',
        ' ### Phase 1: Core Authentication',
        ' - [x] Password hashing with bcrypt',
        ' - [x] Email validation endpoint',
        '-- [ ] JWT token generation',
        '+- [x] JWT token generation',
        '+- [x] Login/logout endpoints',
        ' ',
        '-### Phase 2: OAuth Integration',
        '-- [ ] Google OAuth provider',
        '-- [ ] GitHub OAuth provider',
        '+### Phase 2: OAuth Integration (In Progress)',
        '+- [x] OAuth provider abstraction',
        '+- [ ] Google OAuth provider (in progress)',
        '+- [ ] GitHub OAuth provider (blocked)',
        '+- [ ] OAuth callback handler',
        ' ',
        '+### Phase 3: Session Management',
        '+- [ ] Refresh token rotation',
        '+- [ ] Session listing endpoint',
        '+- [ ] Concurrent session limits',
      ].join('\n'),
    },
    {
      file: 'research.md',
      additions: 42,
      deletions: 0,
      diff: [
        '@@ -0,0 +1,42 @@',
        '+# OAuth 2.0 Research',
        '+ ',
        '+## Provider Comparison',
        '+ ',
        '+### Google OAuth',
        '+- Most widely used',
        '+- Good documentation',
        '+- Supports OIDC',
        '+ ',
        '+### GitHub OAuth',
        '+- Popular for developer tools',
        '+- Simple API',
        '+- No OIDC support',
        '+ ',
        '+## Token Strategy',
        '+ ',
        '+Recommended approach:',
        '+1. Short-lived access tokens (15 min)',
        '+2. Longer refresh tokens (7 days)',
        '+3. Rotate refresh tokens on use',
      ].join('\n'),
    },
  ],
};
